/**
 * Care Matrix data model and protocol seed (Sprint H — Protocol-Driven Reporting).
 *
 * The Care Matrix is the runtime expression of the protocol-driven
 * reporting claim on the preview page: chief complaint + provider
 * impression activate a protocol, which renders as a click-through
 * sequence of steps. Each tap auto-time-stamps the action, runs the
 * dose calculator (when invoked), and writes the structured record
 * to the appropriate Dexie table (medications_given / procedures /
 * vital_sets / etc.). The EMT documents by treating, not by typing.
 *
 * This file is intentionally narrow: ONE seed protocol (RI 4.04 ACS)
 * end-to-end so the pattern is real and demoable. Other protocols
 * follow the same shape and drop in here.
 */

import type { ConsumerId } from './pipeline-graph';
import type { EPCR } from '@/types';
import {
  RI_PROTOCOLS,
  type Protocol as EngineProtocol,
  type ProtocolStep as EngineStep,
  type ProtocolTrigger as EngineTrigger,
} from '@moquintrend/protocol-engine';

/** Step kind drives which Dexie table the click writes to. */
export type StepKind = 'med' | 'procedure' | 'assessment' | 'vital_set' | 'disposition';

interface StepBase {
  /** Stable id for de-dup / re-render. */
  id: string;
  kind: StepKind;
  label: string;
  /** Free-text rationale shown under the step name. */
  hint?: string;
  /** NEMSIS element ids satisfied by this click. */
  nemsis: string[];
  /** Downstream consumers fed by this click. */
  feeds: ConsumerId[];
  /** Optional cascade triggers (rendered as dashed amber hint). */
  triggers?: { consumer: ConsumerId; label: string }[];
}

export interface MedStep extends StepBase {
  kind: 'med';
  drug_name: string;
  dose: string;
  unit: string;
  route: string;
  /** Common patient response options for the chip picker after click. */
  responses?: string[];
  /**
   * Live dose calc — runs against the current EPCR + latest vital set.
   * Returns a string sentence shown inline ("Calc: BP 142/88 ≥ 100, OK
   * to give"). Returns null when no calc applies.
   */
  doseCalc?: (ctx: CalcContext) => string | null;
  /** When non-null, the click is BLOCKED with this reason. */
  doseGate?: (ctx: CalcContext) => string | null;
}

export interface ProcedureStep extends StepBase {
  kind: 'procedure';
  procedure_type: string;
  body_site?: string;
}

export interface AssessmentStep extends StepBase {
  kind: 'assessment';
  /**
   * Slot inside ePCR.set_findings to write to (e.g. 'stemi'), with the
   * key/value to set. Lets a single click record a 12-lead interpretation
   * AND raise the cascade trigger to the PCI alert consumer.
   */
  setFindings?: { slot: string; patch: Record<string, unknown> };
}

export interface VitalSetStep extends StepBase {
  kind: 'vital_set';
}

export interface DispositionStep extends StepBase {
  kind: 'disposition';
  /** Pre-fill destination on click. */
  destination?: string;
  patch?: Partial<EPCR>;
}

export type CareStep = MedStep | ProcedureStep | AssessmentStep | VitalSetStep | DispositionStep;

export interface CareMatrixProtocol {
  /** Stable id (matches NEMSIS eProtocols.01 when applicable). */
  id: string;
  /** Display title shown at the top of the matrix. */
  name: string;
  /**
   * Source-document version (e.g. "2026.01"). Stamped onto the ePCR
   * at the moment the protocol activates so QA / amendment / subpoena
   * review years later can resolve the EXACT version of the protocol
   * that drove the call. Never mutated after capture.
   */
  version: string;
  /** YYYY-MM-DD — date this version of the protocol took effect. */
  effectiveDate: string;
  /** Citation footer (e.g. "RI Statewide Protocol 4.04 v2026.01"). */
  citation: string;
  /**
   * Activation predicate: returns true when this protocol should
   * automatically open. Reads ePCR fields so the trigger logic is
   * data-driven and easy to expand.
   */
  activates: (epcr: EPCR) => boolean;
  /** Ordered care steps. */
  steps: CareStep[];
  /**
   * Provenance: 'handbuilt' = vitals-aware dose gates + calculators
   * audited line-by-line against the source PDF. 'engine' = converted
   * from the engine package; contraindications are warnings only.
   * Surface this to QA so reviewers know which gates were enforced.
   */
  provenance: 'handbuilt' | 'engine';
}

/* ============================================================
 * Calc context — what dose calculators receive
 * ============================================================ */

export interface CalcContext {
  epcr: EPCR;
  /** Latest vital set if any (sorted desc by timestamp). */
  latestVital: {
    bp_systolic: number | null;
    bp_diastolic: number | null;
    heart_rate: number | null;
    heart_rhythm: string | null;
    respiratory_rate: number | null;
    spo2: number | null;
    etco2: number | null;
    blood_glucose: number | null;
    gcs_total: number | null;
  } | null;
  /** Patient weight in kg, if known. */
  weight_kg: number | null;
  /** Whether the patient list contains anything matching the regex (case-insensitive). */
  hasMed: (rx: RegExp) => boolean;
  /**
   * Multi-tap administration history for the CURRENT step. The
   * Care Matrix populates this from medications_given / procedures
   * already on the ePCR before invoking the calc, so the calculator
   * can render running totals + interval timers ("4 mg given so far,
   * next dose due at 14:31 per q3-5 min protocol", etc.).
   *
   * Empty array = step has not yet been performed.
   */
  history: Array<{
    timestamp: string;     // ISO
    /** Dose value as recorded (e.g. "1" for 1 mg, "300" for 300 mg). */
    dose?: string;
    /** Unit as recorded (e.g. "mg", "mcg", "mL"). */
    unit?: string;
  }>;
}

/* ============================================================
 * Calc helpers — small primitives the per-protocol calcs reuse.
 * Keeps the dose-tracking math centralized so a fix lands in one
 * place instead of nine.
 * ============================================================ */

/**
 * Cumulative dose total for a multi-tap step. Sums the numeric
 * portion of `history[i].dose`. Returns 0 if no doses or all parse
 * to NaN. Intended for "max X mg total" gates ("4/10 mg given").
 */
export function cumulativeDose(ctx: CalcContext): number {
  return ctx.history.reduce((sum, h) => {
    const n = parseFloat(h.dose ?? '');
    return Number.isFinite(n) ? sum + n : sum;
  }, 0);
}

/**
 * Minutes since the most recent administration in `ctx.history`.
 * Returns null when the step has not yet been performed. Used for
 * repeat-interval timers ("last dose 4 min ago — wait 1 min").
 */
export function minutesSinceLastDose(ctx: CalcContext): number | null {
  const last = ctx.history[ctx.history.length - 1];
  if (!last) return null;
  const t = new Date(last.timestamp).getTime();
  if (!Number.isFinite(t)) return null;
  return Math.max(0, Math.round((Date.now() - t) / 60000));
}

/**
 * Compose a calc string with running-total + interval awareness for
 * meds with `repeat_interval_min` and/or `max_total_<unit>` constraints.
 * The per-protocol calc passes the units + caps; the helper does the
 * arithmetic and produces a single readable line.
 */
export function repeatableCalc(
  ctx: CalcContext,
  opts: {
    /** Per-dose amount as a number (e.g. 1 for 1 mg) */
    perDose: number;
    /** Unit string used for display ("mg", "mcg", "mL") */
    unit: string;
    /** Minutes between doses per protocol (omit for no interval rule) */
    intervalMin?: number;
    /** Total cumulative cap (omit for no cap) */
    maxTotal?: number;
    /** Max number of doses (omit for no count cap) */
    maxDoses?: number;
    /** Optional descriptive prefix, e.g. "Adult — " */
    prefix?: string;
  },
): string {
  const given = ctx.history.length;
  const total = cumulativeDose(ctx);
  const minsSince = minutesSinceLastDose(ctx);
  const parts: string[] = [];
  if (opts.prefix) parts.push(opts.prefix);
  parts.push(`${opts.perDose} ${opts.unit} per dose.`);
  if (given > 0) {
    parts.push(`Given so far: ${given}${opts.maxDoses ? `/${opts.maxDoses}` : ''} doses, ${total} ${opts.unit} total${opts.maxTotal ? ` of ${opts.maxTotal} ${opts.unit} max` : ''}.`);
    if (opts.intervalMin && minsSince != null) {
      const remaining = opts.intervalMin - minsSince;
      if (remaining > 0) {
        parts.push(`Last dose ${minsSince} min ago — WAIT ${remaining} more min before repeat.`);
      } else {
        parts.push(`Last dose ${minsSince} min ago — OK to repeat now.`);
      }
    }
    if (opts.maxTotal && total >= opts.maxTotal) {
      parts.push(`MAX REACHED. Online medical control for further doses.`);
    } else if (opts.maxDoses && given >= opts.maxDoses) {
      parts.push(`MAX DOSES REACHED. Online medical control for further doses.`);
    }
  }
  return parts.join(' ');
}

/* ============================================================
 * SEED PROTOCOL — RI 4.04 Acute Coronary Syndrome
 * ============================================================ */

const RI_4_04_ACS: CareMatrixProtocol = {
  id: 'ri_4_04_acs',
  name: 'Acute Coronary Syndrome (Cardiac Chest Pain)',
  version: '2026.01',
  effectiveDate: '2026-04-01',
  citation: 'RI Statewide EMS Protocol 4.04 · v2026.01',
  provenance: 'handbuilt',

  activates(epcr) {
    const haystack = [
      epcr.primary_impression,
      epcr.chief_complaint,
      ...(epcr.secondary_impressions ?? []),
    ].filter(Boolean).join(' ').toLowerCase();
    return /chest pain|acute coronary|stemi|ami|cardiac|angina/.test(haystack);
  },

  steps: [
    {
      id: 'asa',
      kind: 'med',
      label: 'Aspirin 324 mg PO chewed',
      hint: 'Per protocol, give as soon as ACS suspected unless allergy or active GI bleed.',
      drug_name: 'Aspirin',
      dose: '324',
      unit: 'mg',
      route: 'PO',
      responses: ['Improved', 'Unchanged', 'Worse', 'Pending'],
      nemsis: ['eMedications.03', 'eMedications.04', 'eMedications.05', 'eMedications.06'],
      feeds: ['nemsis_xml', 'riemsis_api', 'narrative_engine', 'comstar_billing'],
      doseCalc: () => 'Calc: 4 × 81 mg tablets, chewed and swallowed.',
      doseGate: (ctx) => {
        // Block if a documented aspirin allergy is present.
        const allergies = (ctx.epcr as unknown as { allergies?: string[] }).allergies ?? [];
        const a = allergies.join(' ').toLowerCase();
        if (/aspirin|asa\b|nsaid|salicyl/.test(a)) {
          return 'Documented salicylate allergy. Do NOT give. Document refusal reason and proceed.';
        }
        return null;
      },
    },
    {
      id: 'iv_access',
      kind: 'procedure',
      label: 'IV access — 18 g, AC, NS lock',
      hint: 'Saline lock unless hemodynamic instability.',
      procedure_type: 'IV access',
      body_site: 'Left AC',
      nemsis: ['eProcedures.03', 'eProcedures.05', 'eProcedures.06'],
      feeds: ['nemsis_xml', 'narrative_engine', 'comstar_billing'],
    },
    {
      id: 'twelve_lead',
      kind: 'procedure',
      label: '12-lead ECG acquired',
      hint: 'Door-to-EKG benchmark: ≤ 10 min from patient contact.',
      procedure_type: '12-lead ECG',
      nemsis: ['eProcedures.03'],
      feeds: ['nemsis_xml', 'narrative_engine'],
    },
    {
      id: 'stemi_assess',
      kind: 'assessment',
      label: 'STEMI present (interpret 12-lead)',
      hint: 'Tap if ST-elevation in two contiguous leads. Activates PCI center.',
      nemsis: ['eVitals.31'],
      feeds: ['nemsis_xml', 'narrative_engine'],
      triggers: [{
        consumer: 'pci_alert',
        label: 'STEMI assessment activates PCI center pre-arrival',
      }],
      setFindings: {
        slot: 'stemi',
        patch: { interpretation: 'STEMI', noted_at: new Date().toISOString() },
      },
    },
    {
      id: 'ntg',
      kind: 'med',
      label: 'Nitroglycerin 0.4 mg SL',
      hint: 'Single dose if BP ≥ 100 systolic and no PDE-5 inhibitor in last 24 h.',
      drug_name: 'Nitroglycerin',
      dose: '0.4',
      unit: 'mg',
      route: 'SL',
      responses: ['Pain decreased', 'No change', 'Hypotension', 'Headache'],
      nemsis: ['eMedications.03', 'eMedications.04', 'eMedications.05', 'eMedications.06'],
      feeds: ['nemsis_xml', 'riemsis_api', 'narrative_engine', 'comstar_billing'],
      doseCalc: (ctx) => {
        const sbp = ctx.latestVital?.bp_systolic;
        const head = sbp == null
          ? 'Confirm BP ≥ 100 systolic before dosing. '
          : `BP ${sbp} systolic — ${sbp >= 100 ? 'OK to give' : 'BELOW 100, do not give'}. `;
        return head + repeatableCalc(ctx, {
          perDose: 0.4,
          unit: 'mg',
          intervalMin: 5,
          maxDoses: 3,
          prefix: 'Adult (0.4 mg SL, repeat q5 min ×3 if SBP ≥ 100) — ',
        });
      },
      doseGate: (ctx) => {
        const sbp = ctx.latestVital?.bp_systolic;
        if (sbp != null && sbp < 100) {
          return `BP ${sbp} systolic is below the 100-systolic threshold. Withhold NTG; consider IV fluids.`;
        }
        if (ctx.hasMed(/sildenafil|tadalafil|vardenafil|viagra|cialis|levitra/i)) {
          return 'Patient on PDE-5 inhibitor (sildenafil / tadalafil / vardenafil). NTG contraindicated within 24 h. Withhold.';
        }
        return null;
      },
    },
    {
      id: 'repeat_vitals',
      kind: 'vital_set',
      label: 'Repeat vital set (post-NTG)',
      hint: 'Document response to NTG with a fresh vital set.',
      nemsis: ['eVitals.06', 'eVitals.07', 'eVitals.10', 'eVitals.14', 'eVitals.12'],
      feeds: ['nemsis_xml', 'narrative_engine'],
    },
    {
      id: 'transport_pci',
      kind: 'disposition',
      label: 'Transport: closest PCI-capable center (RIH)',
      hint: 'Mission: Lifeline ≤ 90 min first-medical-contact-to-balloon.',
      destination: 'Rhode Island Hospital',
      patch: {
        transport_decision: 'transport_this_unit',
        hospital_choice_reason: 'Closest PCI-capable center for STEMI activation.',
      },
      nemsis: ['eDisposition.01', 'eDisposition.02', 'eDisposition.27'],
      feeds: ['nemsis_xml', 'comstar_billing', 'toc_handoff', 'narrative_engine'],
    },
  ],
};

/* ============================================================
 * SEED PROTOCOL — RI 2.12 Stroke / CVA
 * ============================================================
 * Hand-built. Source: RI Statewide EMS Protocols v2026.01 §2.12.
 * Critical gates:
 *   - BGL < 60 mg/dL → STOP, treat hypoglycemia first; symptoms can
 *     mimic stroke. Hard block on stroke-alert activation until
 *     glucose corrected.
 *   - LKW > 24 h → tPA window definitively closed; thrombectomy
 *     window may still apply (up to 24 h for select LVO). Document
 *     LKW precisely; do not delay transport.
 *   - LAMS ≥ 4 → suggests Large Vessel Occlusion → comprehensive
 *     stroke center (RIH) is preferred destination per Table 2.
 *   - IV access: do NOT delay scene time for IV. Scene time goal
 *     ≤ 10 min for stroke.
 */

const RI_2_12_STROKE: CareMatrixProtocol = {
  id: 'ri-neuro-stroke',
  name: 'Stroke / CVA',
  version: '2026.01',
  effectiveDate: '2026-04-01',
  citation: 'RI Statewide EMS Protocol 2.12 · v2026.01',
  provenance: 'handbuilt',

  activates(epcr) {
    const haystack = [
      epcr.dispatch_reason,
      epcr.chief_complaint,
      epcr.primary_impression,
      ...(epcr.secondary_impressions ?? []),
    ].filter(Boolean).join(' ').toLowerCase();
    return /stroke|cva|cerebrovascular|facial droop|slurred speech|hemiparesis|aphasia/.test(haystack);
  },

  steps: [
    {
      id: 'cincinnati',
      kind: 'assessment',
      label: 'Cincinnati Stroke Scale + LAMS score',
      hint: 'Face: ask to smile. Arms: hold both out, palms up, 10 sec. Speech: repeat a sentence. Score LAMS (face 0/1 + arms 0/1/2 + grip 0/1/2). LAMS ≥ 4 suggests LVO.',
      nemsis: ['eExam.01', 'eExam.20'],
      feeds: ['nemsis_xml', 'narrative_engine'],
      setFindings: { slot: 'stroke', patch: { cincinnati_performed: true } },
    },
    {
      id: 'lkw',
      kind: 'assessment',
      label: 'Last Known Well time documented',
      hint: 'EXACT time the patient was last seen at neurologic baseline. If symptoms noted on waking, document the bedtime — that is the LKW. This drives tPA + thrombectomy eligibility.',
      nemsis: ['eExam.20'],
      feeds: ['nemsis_xml', 'narrative_engine', 'toc_handoff'],
    },
    {
      id: 'glucose',
      kind: 'procedure',
      label: 'Blood glucose check (rule out hypoglycemia)',
      hint: 'Hypoglycemia mimics stroke. BGL < 60 → treat that first; symptoms may resolve. Document BGL even if normal.',
      procedure_type: 'Blood Glucose Analysis',
      nemsis: ['eVitals.18'],
      feeds: ['nemsis_xml', 'narrative_engine'],
    },
    {
      id: 'iv_access',
      kind: 'procedure',
      label: 'IV access (do NOT delay scene time)',
      hint: 'Saline lock if quick. Scene time ≤ 10 min is the gate; abandon IV attempt if it lengthens scene time.',
      procedure_type: 'IV access',
      body_site: 'Left AC',
      nemsis: ['eProcedures.03', 'eProcedures.05'],
      feeds: ['nemsis_xml', 'narrative_engine', 'comstar_billing'],
    },
    {
      id: 'd50',
      kind: 'med',
      label: 'Dextrose 25 g IV (only if BGL < 60)',
      hint: 'Per RI 2.12: if BGL < 60 with stroke-like symptoms, treat hypoglycemia FIRST. Reassess neuro status after correction — symptoms often resolve.',
      drug_name: 'Dextrose 50%',
      dose: '25',
      unit: 'g',
      route: 'IV',
      responses: ['Symptoms resolved', 'Improved', 'Unchanged'],
      nemsis: ['eMedications.03', 'eMedications.04', 'eMedications.05', 'eMedications.06'],
      feeds: ['nemsis_xml', 'riemsis_api', 'narrative_engine', 'comstar_billing'],
      doseGate: (ctx) => {
        const bgl = ctx.latestVital?.blood_glucose;
        if (bgl == null) {
          return 'No BGL on file. Obtain blood glucose BEFORE giving dextrose to confirm hypoglycemia.';
        }
        if (bgl >= 60) {
          return `BGL ${bgl} mg/dL is at or above 60. Dextrose NOT indicated; treat as stroke and transport.`;
        }
        return null;
      },
      doseCalc: (ctx) => {
        const bgl = ctx.latestVital?.blood_glucose;
        if (bgl == null) return 'Calc: confirm BGL < 60 before dosing.';
        return `Calc: BGL ${bgl} mg/dL — ${bgl < 60 ? 'OK to give D50' : 'BGL adequate, do NOT give'}.`;
      },
    },
    {
      id: 'destination',
      kind: 'disposition',
      label: 'Transport to Stroke Center + activate Stroke Alert',
      hint: 'LAMS ≥ 4 → Comprehensive Stroke Center (RIH) for thrombectomy capability. LAMS < 4 → closest Primary Stroke Center is acceptable. Activate Stroke Alert via Comstar / direct facility line.',
      destination: 'Comprehensive Stroke Center',
      patch: {
        transport_decision: 'transport_this_unit',
        hospital_choice_reason: 'Stroke alert — destination per RI Protocol 2.12 Table 2 (LAMS-driven).',
      },
      nemsis: ['eDisposition.01', 'eDisposition.02', 'eDisposition.27'],
      feeds: ['nemsis_xml', 'comstar_billing', 'toc_handoff', 'narrative_engine'],
      triggers: [{
        consumer: 'stroke_alert',
        label: 'Stroke alert activates receiving facility CT + neurology',
      }],
    },
  ],
};

/* ============================================================
 * SEED PROTOCOL — RI 2.04 Allergic Reaction / Anaphylaxis
 * ============================================================
 * Hand-built. Source: RI Statewide EMS Protocols v2026.01 §2.04.
 * Critical gates:
 *   - Anaphylaxis = airway compromise OR SBP < 90 (adult) OR
 *     systemic involvement of ≥ 2 systems. IM epi 0.3 mg
 *     anterolateral thigh is FIRST LINE, BEFORE IV / Benadryl /
 *     albuterol. Block diphenhydramine if epi indicated and not yet
 *     given.
 *   - Pediatric epi: 0.01 mg/kg IM, max 0.3 mg per dose.
 *   - Repeat epi q5min if no improvement, max 3 doses BLS standing
 *     order, additional doses require online medical control.
 *   - Hypotension → NS bolus 20 mL/kg after epi.
 *   - PDE-5 inhibitor caution does NOT apply (this is epi, not NTG).
 */

const RI_2_04_ANAPHYLAXIS: CareMatrixProtocol = {
  id: 'ri-medical-anaphylaxis',
  name: 'Allergic Reaction / Anaphylaxis',
  version: '2026.01',
  effectiveDate: '2026-04-01',
  citation: 'RI Statewide EMS Protocol 2.04 · v2026.01',
  provenance: 'handbuilt',

  activates(epcr) {
    const haystack = [
      epcr.dispatch_reason,
      epcr.chief_complaint,
      epcr.primary_impression,
      ...(epcr.secondary_impressions ?? []),
    ].filter(Boolean).join(' ').toLowerCase();
    return /allerg|anaphyla|hives|urticaria|angioedema|bee sting|wasp|peanut|shellfish/.test(haystack);
  },

  steps: [
    {
      id: 'identify_allergen',
      kind: 'assessment',
      label: 'Identify trigger + remove ongoing exposure',
      hint: 'Bee/wasp: scrape stinger out (do not pinch). Food: stop ingestion. Drug: stop infusion. Environmental: move patient.',
      nemsis: ['eHistory.06', 'eExam.01'],
      feeds: ['nemsis_xml', 'narrative_engine'],
    },
    {
      id: 'severity_assess',
      kind: 'assessment',
      label: 'Severity assessment (mild / moderate / severe)',
      hint: 'SEVERE = airway compromise (stridor, hoarseness, tongue swelling) OR SBP < 90 OR ≥ 2 system involvement (skin + GI, skin + resp). Severe → epi NOW.',
      nemsis: ['eExam.01'],
      feeds: ['nemsis_xml', 'narrative_engine'],
      setFindings: { slot: 'anaphylaxis', patch: { severity_assessed: true } },
    },
    {
      id: 'epi_im',
      kind: 'med',
      label: 'Epinephrine 1:1,000 0.3 mg IM (anterolateral thigh)',
      hint: 'FIRST-LINE for anaphylaxis. Anterolateral thigh, through clothing if needed. Pediatric: 0.01 mg/kg, max 0.3 mg. Repeat q5min if no improvement, max 3 doses standing order.',
      drug_name: 'Epinephrine 1:1,000',
      dose: '0.3',
      unit: 'mg',
      route: 'IM',
      responses: ['Improved', 'Partial response', 'No change', 'Worse — repeat'],
      nemsis: ['eMedications.03', 'eMedications.04', 'eMedications.05', 'eMedications.06'],
      feeds: ['nemsis_xml', 'riemsis_api', 'narrative_engine', 'comstar_billing'],
      doseCalc: (ctx) => {
        if (ctx.weight_kg != null && ctx.weight_kg < 30) {
          const peds = Math.min(0.3, +(ctx.weight_kg * 0.01).toFixed(2));
          return repeatableCalc(ctx, {
            perDose: peds,
            unit: 'mg',
            intervalMin: 5,
            maxDoses: 3,
            prefix: `Pediatric (${ctx.weight_kg} kg, 0.01 mg/kg max 0.3 mg, anterolateral thigh) — `,
          });
        }
        return repeatableCalc(ctx, {
          perDose: 0.3,
          unit: 'mg',
          intervalMin: 5,
          maxDoses: 3,
          prefix: 'Adult (0.3 mg IM anterolateral thigh, repeat q5 min standing order ×3) — ',
        });
      },
      doseGate: (ctx) => {
        const allergies = (ctx.epcr as unknown as { allergies?: string[] }).allergies ?? [];
        if (/epinephrine|epi\b/i.test(allergies.join(' '))) {
          return 'Documented epinephrine allergy is exceedingly rare and does not contraindicate epi in true anaphylaxis. Confirm allergy validity with online medical control if uncertain.';
        }
        return null;
      },
    },
    {
      id: 'iv_access',
      kind: 'procedure',
      label: 'IV access — large bore (16–18 g)',
      hint: 'Anticipate hypotension. Two lines if SBP < 90 or worsening.',
      procedure_type: 'IV access',
      body_site: 'AC',
      nemsis: ['eProcedures.03', 'eProcedures.05'],
      feeds: ['nemsis_xml', 'narrative_engine', 'comstar_billing'],
    },
    {
      id: 'fluid_bolus',
      kind: 'med',
      label: 'NS 20 mL/kg IV bolus (if SBP < 90)',
      hint: 'For hypotensive anaphylaxis after epi. Adult typical: 1 L wide open. Reassess after each 250 mL.',
      drug_name: 'Normal Saline',
      dose: '1000',
      unit: 'mL',
      route: 'IV bolus',
      responses: ['BP improved', 'BP unchanged', 'Bolus complete'],
      nemsis: ['eMedications.03', 'eMedications.04', 'eMedications.05', 'eMedications.06'],
      feeds: ['nemsis_xml', 'narrative_engine', 'comstar_billing'],
      doseGate: (ctx) => {
        const sbp = ctx.latestVital?.bp_systolic;
        if (sbp == null) {
          return 'No BP on file. Confirm hypotension (SBP < 90) before bolus.';
        }
        if (sbp >= 90) {
          return `BP ${sbp} systolic is at or above 90. Bolus not indicated unless trending down — reassess.`;
        }
        return null;
      },
      doseCalc: (ctx) => {
        if (ctx.weight_kg == null) return 'Calc: adult 1 L wide open. Pediatric: 20 mL/kg, weight first.';
        const ml = Math.round(ctx.weight_kg * 20);
        return `Calc: ${ctx.weight_kg} kg × 20 mL/kg = ${ml} mL bolus.`;
      },
    },
    {
      id: 'albuterol',
      kind: 'med',
      label: 'Albuterol 2.5 mg nebulized (if bronchospasm)',
      hint: 'Adjunct ONLY when wheezing present. Does not replace epi. May add ipratropium 0.5 mg first round.',
      drug_name: 'Albuterol',
      dose: '2.5',
      unit: 'mg',
      route: 'Nebulized',
      responses: ['Wheezing improved', 'Wheezing unchanged', 'Worse'],
      nemsis: ['eMedications.03', 'eMedications.04', 'eMedications.05', 'eMedications.06'],
      feeds: ['nemsis_xml', 'riemsis_api', 'narrative_engine', 'comstar_billing'],
    },
    {
      id: 'diphenhydramine',
      kind: 'med',
      label: 'Diphenhydramine 50 mg IV/IM',
      hint: 'Adjunct ONLY. NOT a substitute for epi in anaphylaxis. Helps with hives/itching after epi has stabilized airway/BP. Pediatric: 1 mg/kg, max 50 mg.',
      drug_name: 'Diphenhydramine',
      dose: '50',
      unit: 'mg',
      route: 'IV',
      responses: ['Itching improved', 'Hives improved', 'No change'],
      nemsis: ['eMedications.03', 'eMedications.04', 'eMedications.05', 'eMedications.06'],
      feeds: ['nemsis_xml', 'riemsis_api', 'narrative_engine', 'comstar_billing'],
      doseCalc: (ctx) => {
        if (ctx.weight_kg == null || ctx.weight_kg >= 50) return 'Calc: adult 50 mg IV/IM.';
        const peds = Math.min(50, Math.round(ctx.weight_kg));
        return `Calc: pediatric (${ctx.weight_kg} kg) — ${peds} mg (1 mg/kg, max 50 mg).`;
      },
    },
    {
      id: 'transport',
      kind: 'disposition',
      label: 'Transport — closest appropriate ED',
      hint: 'All anaphylaxis transports, even after resolution post-epi (biphasic reaction risk 4–8 h later). Reassess vitals q5min en route.',
      patch: {
        transport_decision: 'transport_this_unit',
        hospital_choice_reason: 'Anaphylaxis — observation for biphasic reaction per RI 2.04.',
      },
      nemsis: ['eDisposition.01', 'eDisposition.02'],
      feeds: ['nemsis_xml', 'comstar_billing', 'toc_handoff', 'narrative_engine'],
    },
  ],
};

/* ============================================================
 * SEED PROTOCOL — RI 3.03 Cardiac Arrest (Adult)
 * ============================================================
 * Hand-built. Source: RI Statewide EMS Protocols v2026.01 §3.03
 * with §5.01 (capnography) overlay.
 *
 * Critical gates:
 *   - Defibrillation step is gated to shockable rhythms only
 *     (VF / pulseless VT). The latest vital_set's heart_rhythm field
 *     is consulted; missing → soft warn.
 *   - Epinephrine 1 mg IV/IO q3-5min — given for ALL arrest rhythms,
 *     but the calc shows the time since last dose so the user
 *     doesn't redose too early. Pediatric: 0.01 mg/kg, max 1 mg.
 *   - Amiodarone 300 mg IV/IO bolus FIRST DOSE only after refractory
 *     VF/pVT (≥ 2 defibrillations). Soft block if rhythm not shockable.
 *   - Advanced airway requires §5.01 waveform capnography
 *     confirmation; the assessment step writes to set_findings.
 *   - ROSC step: BP ≥ 90 systolic + palpable pulse + organized rhythm.
 */

const RI_3_03_CARDIAC_ARREST: CareMatrixProtocol = {
  id: 'ri-cardiac-arrest',
  name: 'Cardiac Arrest (Adult)',
  version: '2026.01',
  effectiveDate: '2026-04-01',
  citation: 'RI Statewide EMS Protocol 3.03 (with §5.01 capnography) · v2026.01',
  provenance: 'handbuilt',

  activates(epcr) {
    const haystack = [
      epcr.dispatch_reason,
      epcr.chief_complaint,
      epcr.primary_impression,
      ...(epcr.secondary_impressions ?? []),
    ].filter(Boolean).join(' ').toLowerCase();
    return /cardiac arrest|pulseless|cpr in progress|asystole|vfib|v-fib|pulseless vt|pea|rosc/.test(haystack);
  },

  steps: [
    {
      id: 'cpr',
      kind: 'procedure',
      label: 'High-quality CPR (100–120/min, 2" depth, full recoil)',
      hint: 'Compressor swap q2 min. Minimize pause. Audible metronome on monitor or counter.',
      procedure_type: 'CPR',
      nemsis: ['eProcedures.03', 'eArrest.09'],
      feeds: ['nemsis_xml', 'narrative_engine', 'comstar_billing'],
    },
    {
      id: 'monitor',
      kind: 'procedure',
      label: 'Defib pads on, analyze rhythm',
      hint: 'AP (anterior-posterior) placement preferred for arrest. Limit pause to ≤ 5 sec for rhythm check.',
      procedure_type: 'Cardiac Monitoring',
      nemsis: ['eProcedures.03', 'eArrest.10'],
      feeds: ['nemsis_xml', 'narrative_engine', 'comstar_billing'],
    },
    {
      id: 'rhythm_assessment',
      kind: 'assessment',
      label: 'Rhythm interpretation (shockable vs non-shockable)',
      hint: 'Shockable: VF / pulseless VT. Non-shockable: asystole / PEA. Document on the vital set so downstream steps gate correctly.',
      nemsis: ['eArrest.10'],
      feeds: ['nemsis_xml', 'narrative_engine'],
      setFindings: { slot: 'cardiac_arrest', patch: { rhythm_assessed_at: new Date().toISOString() } },
    },
    {
      id: 'defib',
      kind: 'procedure',
      label: 'Defibrillate at maximum biphasic energy (200J)',
      hint: 'ONLY for VF / pulseless VT. Resume CPR IMMEDIATELY for 2 min — do NOT pause for pulse check post-shock.',
      procedure_type: 'Defibrillation',
      nemsis: ['eProcedures.03', 'eArrest.13'],
      feeds: ['nemsis_xml', 'narrative_engine', 'comstar_billing'],
    },
    {
      id: 'iv_io',
      kind: 'procedure',
      label: 'IV / IO access (IO preferred if no IV in 90 sec)',
      hint: 'IO sites: proximal humerus (preferred), proximal tibia, distal femur (peds).',
      procedure_type: 'IV access',
      nemsis: ['eProcedures.03', 'eProcedures.05'],
      feeds: ['nemsis_xml', 'narrative_engine', 'comstar_billing'],
    },
    {
      id: 'epi',
      kind: 'med',
      label: 'Epinephrine 1:10,000 — 1 mg IV/IO q3-5min',
      hint: 'Give for ALL arrest rhythms. For shockable rhythms, give AFTER 2nd defibrillation. Repeat q3-5 min throughout arrest. Pediatric: 0.01 mg/kg.',
      drug_name: 'Epinephrine 1:10,000',
      dose: '1',
      unit: 'mg',
      route: 'IV/IO',
      responses: ['Given', 'No change', 'ROSC'],
      nemsis: ['eMedications.03', 'eMedications.04', 'eMedications.05', 'eMedications.06'],
      feeds: ['nemsis_xml', 'riemsis_api', 'narrative_engine', 'comstar_billing'],
      doseCalc: (ctx) => {
        if (ctx.weight_kg != null && ctx.weight_kg < 30) {
          const peds = +(ctx.weight_kg * 0.01).toFixed(2);
          return repeatableCalc(ctx, {
            perDose: peds,
            unit: 'mg',
            intervalMin: 3,
            prefix: `Pediatric (${ctx.weight_kg} kg, 0.01 mg/kg max 1 mg) — `,
          });
        }
        return repeatableCalc(ctx, {
          perDose: 1,
          unit: 'mg',
          intervalMin: 3,
          prefix: 'Adult (1 mg IV/IO, 10 mL of 1:10,000, repeat q3-5 min) — ',
        });
      },
    },
    {
      id: 'amio',
      kind: 'med',
      label: 'Amiodarone 300 mg IV/IO bolus (first dose)',
      hint: 'ONLY for refractory VF/pVT after ≥ 2 defibrillations. Second dose: 150 mg if still refractory. Pediatric: 5 mg/kg.',
      drug_name: 'Amiodarone',
      dose: '300',
      unit: 'mg',
      route: 'IV/IO',
      responses: ['Given', 'Rhythm converted', 'No change'],
      nemsis: ['eMedications.03', 'eMedications.04', 'eMedications.05', 'eMedications.06'],
      feeds: ['nemsis_xml', 'riemsis_api', 'narrative_engine', 'comstar_billing'],
      doseGate: (ctx) => {
        const r = ctx.latestVital?.heart_rhythm?.toLowerCase() ?? '';
        if (!r) return 'Confirm rhythm is VF or pulseless VT and that ≥ 2 defibrillations have been delivered before giving amiodarone.';
        if (!/vf|v-fib|ventricular fib|pulseless vt|v-tach|vtach/.test(r)) {
          return `Documented rhythm "${ctx.latestVital?.heart_rhythm}" is NOT shockable. Amiodarone is indicated only for refractory VF/pVT.`;
        }
        return null;
      },
      doseCalc: (ctx) => {
        // First dose 300 mg, second 150 mg, max 450 mg total per
        // protocol. The repeatable-calc helper renders running total
        // against the 450 mg cap; the per-dose value flips after the
        // first administration.
        const given = ctx.history.length;
        if (ctx.weight_kg != null && ctx.weight_kg < 30) {
          const peds = Math.round(ctx.weight_kg * 5);
          return repeatableCalc(ctx, {
            perDose: peds,
            unit: 'mg',
            maxDoses: 1,
            prefix: `Pediatric (${ctx.weight_kg} kg, 5 mg/kg max 300 mg) — `,
          });
        }
        const nextDose = given === 0 ? 300 : 150;
        return repeatableCalc(ctx, {
          perDose: nextDose,
          unit: 'mg',
          maxTotal: 450,
          maxDoses: 2,
          prefix: `Adult (${given === 0 ? 'first dose 300 mg' : 'second/refractory 150 mg'}, max 450 mg total) — `,
        });
      },
    },
    {
      id: 'airway',
      kind: 'procedure',
      label: 'Advanced airway (i-Gel or ETT) — capnography MANDATORY',
      hint: 'Per §5.01: waveform EtCO2 confirms placement AND quality of CPR. Sustained EtCO2 < 10 mmHg suggests poor compressions. Upload waveform tracing post-call.',
      procedure_type: 'Supraglottic Airway',
      nemsis: ['eProcedures.03', 'eAirway.02'],
      feeds: ['nemsis_xml', 'narrative_engine', 'comstar_billing'],
    },
    {
      id: 'airway_confirm',
      kind: 'assessment',
      label: 'Capnography waveform confirmed — placement verified',
      hint: 'Per §5.01: waveform required for advanced-airway PCR. Tap when continuous EtCO2 trace confirms tracheal placement. Post-call: upload tracing image.',
      nemsis: ['eAirway.02', 'eVitals.16'],
      feeds: ['nemsis_xml', 'narrative_engine'],
      setFindings: { slot: 'cardiac_arrest', patch: { advanced_airway_placed: true, capnography_confirmed: true } },
    },
    {
      id: 'rosc',
      kind: 'assessment',
      label: 'ROSC achieved (return of spontaneous circulation)',
      hint: 'Criteria: palpable pulse + organized rhythm + sustained EtCO2 ≥ 35 mmHg. Tap when ALL three present. Triggers post-arrest care path.',
      nemsis: ['eArrest.18'],
      feeds: ['nemsis_xml', 'narrative_engine'],
      triggers: [{
        consumer: 'pci_alert',
        label: 'ROSC after VF/pVT — consider PCI center for cath lab',
      }],
      setFindings: { slot: 'cardiac_arrest', patch: { rosc_achieved: true, rosc_time: new Date().toISOString() } },
    },
    {
      id: 'transport',
      kind: 'disposition',
      label: 'Transport — closest appropriate facility',
      hint: 'ROSC + suspected cardiac etiology → PCI-capable center. No ROSC → continue resuscitation per medical control / DNR review.',
      patch: {
        transport_decision: 'transport_this_unit',
        hospital_choice_reason: 'Cardiac arrest — destination per RI 3.03 / ROSC status.',
      },
      nemsis: ['eDisposition.01', 'eDisposition.02', 'eDisposition.27'],
      feeds: ['nemsis_xml', 'comstar_billing', 'toc_handoff', 'narrative_engine'],
    },
  ],
};

/* ============================================================
 * SEED PROTOCOL — RI 4.18 Overdose / Toxicology (Opioid)
 * ============================================================
 * Hand-built. Source: RI Statewide EMS Protocols v2026.01 §4.18
 * with §6.11 (Naloxone Leave-Behind) overlay.
 *
 * Critical gates:
 *   - Naloxone is BLOCKED unless respiratory depression OR altered
 *     mental status with suspected opioid OD. Awake patients with
 *     adequate respirations do NOT get naloxone (risk: precipitated
 *     withdrawal, agitation, vomiting, aspiration).
 *   - Pediatric naloxone: 0.1 mg/kg IN/IV/IM (max 2 mg per dose).
 *   - BVM only if RR < 8 OR SpO2 < 90%.
 *   - Repeat naloxone q2-3 min, max 10 mg total per RI standing order
 *     (online medical control for further doses).
 *   - Re-sedation watch: naloxone half-life 30-90 min, fentanyl half-
 *     life can exceed that. Patients refusing transport post-naloxone
 *     get the leave-behind kit per §6.11 + documented refusal.
 *   - Destination: OUD Preferred Facility for transports (RI 4.18).
 */

const RI_4_18_OVERDOSE: CareMatrixProtocol = {
  id: 'ri-medical-overdose',
  name: 'Overdose / Toxicology (Opioid focus)',
  version: '2026.01',
  effectiveDate: '2026-04-01',
  citation: 'RI Statewide EMS Protocol 4.18 (with §6.11 leave-behind) · v2026.01',
  provenance: 'handbuilt',

  activates(epcr) {
    const haystack = [
      epcr.dispatch_reason,
      epcr.chief_complaint,
      epcr.primary_impression,
      ...(epcr.secondary_impressions ?? []),
    ].filter(Boolean).join(' ').toLowerCase();
    return /overdose|opioid|opiate|fentanyl|heroin|narcan|naloxone|poisoning|ingestion|toxic/.test(haystack);
  },

  steps: [
    {
      id: 'scene_safety',
      kind: 'assessment',
      label: 'Scene safety — needles, paraphernalia, fentanyl powder',
      hint: 'Fentanyl powder is dermally absorbable in high concentration. Gloves + N95 if powder visible. Bring patient to fresh air.',
      nemsis: ['eScene.06'],
      feeds: ['nemsis_xml', 'narrative_engine'],
    },
    {
      id: 'respiratory_assess',
      kind: 'assessment',
      label: 'Respiratory + mental status assessment',
      hint: 'OPIOID TOXIDROME: ↓ RR, pinpoint pupils, ↓ LOC. Document RR, SpO2, GCS, pupil size. These gate the naloxone decision.',
      nemsis: ['eExam.01', 'eVitals.10', 'eVitals.14'],
      feeds: ['nemsis_xml', 'narrative_engine'],
    },
    {
      id: 'bvm',
      kind: 'procedure',
      label: 'Assist ventilations with BVM (if RR < 8 or SpO2 < 90%)',
      hint: '8–10 breaths/min, watch for chest rise. Continue until naloxone takes effect or spontaneous adequate respirations.',
      procedure_type: 'BVM Ventilation',
      nemsis: ['eProcedures.03'],
      feeds: ['nemsis_xml', 'narrative_engine', 'comstar_billing'],
    },
    {
      id: 'naloxone',
      kind: 'med',
      label: 'Naloxone 4 mg IN (or 0.4 mg IV/IM)',
      hint: 'IN preferred for safety + speed (no needle exposure). Repeat q2-3 min if no response, up to 10 mg total per standing order. Patient may wake combative — be ready.',
      drug_name: 'Naloxone',
      dose: '4',
      unit: 'mg',
      route: 'IN',
      responses: ['Improved RR', 'Improved LOC', 'Both improved', 'No response — repeat', 'Combative'],
      nemsis: ['eMedications.03', 'eMedications.04', 'eMedications.05', 'eMedications.06'],
      feeds: ['nemsis_xml', 'riemsis_api', 'narrative_engine', 'comstar_billing'],
      doseGate: (ctx) => {
        const rr = ctx.latestVital?.respiratory_rate;
        const spo2 = ctx.latestVital?.spo2;
        const gcs = ctx.latestVital?.gcs_total;
        // Block if patient is awake + breathing adequately. Naloxone
        // is for opioid-induced respiratory/mental depression, not
        // for "patient took drugs and is fine".
        const adequateRR = rr != null && rr >= 12;
        const adequateSpo2 = spo2 != null && spo2 >= 94;
        const awake = gcs != null && gcs >= 14;
        if (rr == null && spo2 == null && gcs == null) {
          return 'Document RR, SpO2, and GCS BEFORE giving naloxone. The protocol gates dosing on respiratory/mental depression.';
        }
        if (adequateRR && adequateSpo2 && awake) {
          return `Patient is awake (GCS ${gcs}) with adequate respirations (RR ${rr}, SpO2 ${spo2}%). Naloxone NOT indicated — risk of precipitated withdrawal. Monitor instead.`;
        }
        return null;
      },
      doseCalc: (ctx) => {
        if (ctx.weight_kg != null && ctx.weight_kg < 30) {
          const peds = Math.min(2, +(ctx.weight_kg * 0.1).toFixed(1));
          return repeatableCalc(ctx, {
            perDose: peds,
            unit: 'mg',
            intervalMin: 2,
            maxTotal: 10,
            prefix: `Pediatric (${ctx.weight_kg} kg, 0.1 mg/kg max 2 mg) — `,
          });
        }
        return repeatableCalc(ctx, {
          perDose: 4,
          unit: 'mg',
          intervalMin: 2,
          maxTotal: 10,
          prefix: 'Adult (4 mg IN per device, repeat q2-3 min) — ',
        });
      },
    },
    {
      id: 'monitor_resedation',
      kind: 'assessment',
      label: 'Monitor for re-sedation (continuous)',
      hint: 'Naloxone half-life 30–90 min. Fentanyl + sustained-release opioids may outlast it. Recheck RR + SpO2 + LOC q5 min.',
      nemsis: ['eVitals.06'],
      feeds: ['nemsis_xml', 'narrative_engine'],
    },
    {
      id: 'leave_behind',
      kind: 'assessment',
      label: 'Naloxone Leave-Behind kit offered (if refusing transport)',
      hint: 'Per RI §6.11: any opioid OD patient who refuses transport gets a Narcan kit + brief training. Document acceptance/decline and recipient.',
      nemsis: ['eDisposition.04'],
      feeds: ['nemsis_xml', 'narrative_engine'],
      setFindings: { slot: 'overdose', patch: { leave_behind_offered_at: new Date().toISOString() } },
    },
    {
      id: 'transport',
      kind: 'disposition',
      label: 'Transport — OUD Preferred Facility (or BH Link)',
      hint: 'Per RI 4.18: transport to OUD Preferred Facility when patient consents. BH Link option for patients ready to engage with treatment. Closest ED otherwise.',
      destination: 'OUD Preferred Facility',
      patch: {
        transport_decision: 'transport_this_unit',
        hospital_choice_reason: 'Opioid OD — OUD Preferred Facility per RI 4.18.',
      },
      nemsis: ['eDisposition.01', 'eDisposition.02'],
      feeds: ['nemsis_xml', 'comstar_billing', 'toc_handoff', 'narrative_engine'],
    },
  ],
};

/* ============================================================
 * SEED PROTOCOL — RI 2.08 Respiratory Distress / Asthma / COPD
 * ============================================================
 * Hand-built. Source: RI Statewide EMS Protocols v2026.01 §2.08.
 *
 * Critical gates:
 *   - Albuterol/Ipratropium: standard dosing, no vitals gate other
 *     than confirming bronchospasm on auscultation.
 *   - CPAP HARD-BLOCKS on protocol contraindications: vomiting,
 *     altered mental status (GCS < 14), suspected pneumothorax,
 *     SBP < 90. Any one of these blocks placement.
 *   - IM Epi 0.3 mg for severe bronchospasm refractory to nebs OR
 *     impending arrest. Pediatric: 0.01 mg/kg, max 0.3 mg.
 *   - SpO2 < 90 despite max nebs is the gate that surfaces CPAP.
 *   - EtCO2 monitoring documented for all moderate/severe distress.
 */

const RI_2_08_RESPIRATORY: CareMatrixProtocol = {
  id: 'ri-respiratory-distress',
  name: 'Respiratory Distress / Asthma / COPD',
  version: '2026.01',
  effectiveDate: '2026-04-01',
  citation: 'RI Statewide EMS Protocol 2.08 · v2026.01',
  provenance: 'handbuilt',

  activates(epcr) {
    const haystack = [
      epcr.dispatch_reason,
      epcr.chief_complaint,
      epcr.primary_impression,
      ...(epcr.secondary_impressions ?? []),
    ].filter(Boolean).join(' ').toLowerCase();
    return /shortness of breath|sob|breathing problem|dyspnea|asthma|copd|wheez|bronchospasm|respiratory distress|chf|pulmonary edema/.test(haystack);
  },

  steps: [
    {
      id: 'abc_lung_sounds',
      kind: 'assessment',
      label: 'Airway / Breathing assessment + bilateral lung sounds',
      hint: 'Auscultate APEX + BASE bilaterally. Document: clear / wheezes / crackles / rhonchi / diminished / absent. Note tripod posture, accessory-muscle use, retractions, 1-word dyspnea.',
      nemsis: ['eExam.16'],
      feeds: ['nemsis_xml', 'narrative_engine'],
    },
    {
      id: 'monitoring',
      kind: 'procedure',
      label: 'SpO2 + EtCO2 + cardiac monitoring',
      hint: 'EtCO2 waveform shows shark-fin morphology in bronchospasm — diagnostic + trends with treatment response.',
      procedure_type: 'Pulse Oximetry / Capnography',
      nemsis: ['eVitals.10', 'eVitals.16'],
      feeds: ['nemsis_xml', 'narrative_engine'],
    },
    {
      id: 'albuterol',
      kind: 'med',
      label: 'Albuterol 2.5 mg nebulized',
      hint: 'Repeat ×3 back-to-back (continuous if severe). Pediatric same dose. Monitor HR — modest tachycardia expected.',
      drug_name: 'Albuterol',
      dose: '2.5',
      unit: 'mg',
      route: 'Nebulized',
      responses: ['Wheezing improved', 'Wheezing unchanged', 'Worse', 'Patient tolerating poorly'],
      nemsis: ['eMedications.03', 'eMedications.04', 'eMedications.05', 'eMedications.06'],
      feeds: ['nemsis_xml', 'riemsis_api', 'narrative_engine', 'comstar_billing'],
      doseCalc: (ctx) => repeatableCalc(ctx, {
        perDose: 2.5,
        unit: 'mg',
        maxDoses: 3,
        prefix: 'Adult & pediatric — 2.5 mg nebulized, repeat ×3 standing order. ',
      }),
    },
    {
      id: 'ipratropium',
      kind: 'med',
      label: 'Ipratropium 0.5 mg nebulized (FIRST neb only)',
      hint: 'Combined with first albuterol treatment. Do not repeat — single-dose adjunct. Pediatric: 0.25 mg.',
      drug_name: 'Ipratropium',
      dose: '0.5',
      unit: 'mg',
      route: 'Nebulized',
      responses: ['Given with first neb', 'Wheezing improved'],
      nemsis: ['eMedications.03', 'eMedications.04', 'eMedications.05', 'eMedications.06'],
      feeds: ['nemsis_xml', 'riemsis_api', 'narrative_engine', 'comstar_billing'],
      doseCalc: (ctx) => {
        if (ctx.weight_kg != null && ctx.weight_kg < 30) {
          return `Calc: pediatric (${ctx.weight_kg} kg) — 0.25 mg nebulized.`;
        }
        return 'Calc: adult — 0.5 mg nebulized, single dose with first albuterol.';
      },
    },
    {
      id: 'cpap',
      kind: 'procedure',
      label: 'CPAP at 5–10 cmH2O (if SpO2 < 90% despite nebs)',
      hint: 'Start at 5, titrate up to 10. Coach the patient through the seal — claustrophobia-failure is common in the first 60 sec.',
      procedure_type: 'CPAP',
      nemsis: ['eProcedures.03', 'eProcedures.05'],
      feeds: ['nemsis_xml', 'narrative_engine', 'comstar_billing'],
    },
    {
      id: 'epi_im',
      kind: 'med',
      label: 'Epinephrine 1:1,000 0.3 mg IM (severe / impending arrest)',
      hint: 'For severe bronchospasm refractory to nebs, anaphylactic component, or impending respiratory arrest. Anterolateral thigh. Pediatric: 0.01 mg/kg, max 0.3 mg.',
      drug_name: 'Epinephrine 1:1,000',
      dose: '0.3',
      unit: 'mg',
      route: 'IM',
      responses: ['Wheezing improved', 'Work of breathing improved', 'No change'],
      nemsis: ['eMedications.03', 'eMedications.04', 'eMedications.05', 'eMedications.06'],
      feeds: ['nemsis_xml', 'riemsis_api', 'narrative_engine', 'comstar_billing'],
      doseCalc: (ctx) => {
        if (ctx.weight_kg != null && ctx.weight_kg < 30) {
          const peds = Math.min(0.3, +(ctx.weight_kg * 0.01).toFixed(2));
          return repeatableCalc(ctx, {
            perDose: peds,
            unit: 'mg',
            intervalMin: 5,
            maxDoses: 2,
            prefix: `Pediatric (${ctx.weight_kg} kg, 0.01 mg/kg max 0.3 mg) — `,
          });
        }
        return repeatableCalc(ctx, {
          perDose: 0.3,
          unit: 'mg',
          intervalMin: 5,
          maxDoses: 2,
          prefix: 'Adult (0.3 mg IM anterolateral thigh) — ',
        });
      },
    },
    {
      id: 'transport',
      kind: 'disposition',
      label: 'Transport — closest appropriate ED',
      hint: 'CPAP en route if applied; do not remove for transfer. Reassess vitals + auscultation q5min.',
      patch: {
        transport_decision: 'transport_this_unit',
        hospital_choice_reason: 'Respiratory distress — closest ED with respiratory care capability.',
      },
      nemsis: ['eDisposition.01', 'eDisposition.02'],
      feeds: ['nemsis_xml', 'comstar_billing', 'toc_handoff', 'narrative_engine'],
    },
  ],
};

/* ============================================================
 * SEED PROTOCOL — RI 4.04 / 4.01 Trauma + Hemorrhage Control
 * ============================================================
 * Hand-built. Source: RI Statewide EMS Protocols v2026.01 §4.04
 * (hemorrhage) + §4.01 (trauma POE) + §4.03 (spinal motion).
 *
 * Critical gates:
 *   - Tourniquet placement REQUIRED for extremity hemorrhage not
 *     controlled by direct pressure within 60 sec. Time of placement
 *     is captured (downstream feeds → narrative + receiving facility
 *     so OR can plan limb salvage windows).
 *   - Permissive hypotension: target SBP ~90 in penetrating trauma
 *     (no head injury). 1L NS bolus if SBP < 90; reassess after each
 *     250 mL.
 *   - Fentanyl HARD-BLOCKS on SBP < 90 OR GCS < 14 OR respiratory
 *     depression (RR < 10 or SpO2 < 92).
 *   - Spinal motion restriction per RI 4.03 criteria (mechanism +
 *     exam findings); document indication or rule-out reason.
 *   - Scene time ≤ 10 min for critical trauma — surfaced as a
 *     timing flag in the narrative.
 *   - Destination per RI 4.01 Table 2: Level 1 Trauma Center for
 *     physiologic / anatomic / mechanism criteria match.
 */

const RI_4_04_TRAUMA: CareMatrixProtocol = {
  id: 'ri-trauma-hemorrhage',
  name: 'Trauma / Hemorrhage Control',
  version: '2026.01',
  effectiveDate: '2026-04-01',
  citation: 'RI Statewide EMS Protocol 4.04 / 4.01 / 4.03 · v2026.01',
  provenance: 'handbuilt',

  activates(epcr) {
    const haystack = [
      epcr.dispatch_reason,
      epcr.chief_complaint,
      epcr.primary_impression,
      ...(epcr.secondary_impressions ?? []),
    ].filter(Boolean).join(' ').toLowerCase();
    return /trauma|hemorrhage|bleeding|gunshot|gsw|stab|laceration|mvc|mvc rollover|fall from|crush|amputation|burn|penetrating|blunt force/.test(haystack);
  },

  steps: [
    {
      id: 'scene_safety_moi',
      kind: 'assessment',
      label: 'Scene safety + Mechanism of Injury',
      hint: 'BSI. Document MOI precisely (vehicle speed, fall height, weapon type, time of injury). MOI drives destination decision per RI 4.01.',
      nemsis: ['eScene.06', 'eInjury.01', 'eInjury.03'],
      feeds: ['nemsis_xml', 'narrative_engine'],
    },
    {
      id: 'hemorrhage_control',
      kind: 'procedure',
      label: 'Direct pressure → tourniquet if extremity bleed uncontrolled',
      hint: 'Direct pressure × 60 sec. If still bleeding on extremity → tourniquet 2–3" above wound, tighten until bleeding stops, MARK TIME. Junctional or non-extremity → wound packing + pressure.',
      procedure_type: 'Hemorrhage Control - Tourniquet',
      nemsis: ['eProcedures.03', 'eProcedures.05'],
      feeds: ['nemsis_xml', 'narrative_engine', 'comstar_billing', 'toc_handoff'],
    },
    {
      id: 'spinal',
      kind: 'assessment',
      label: 'Spinal motion restriction decision (per RI 4.03 criteria)',
      hint: 'Apply C-collar + LBB/scoop if: midline spinal tenderness, neuro deficit, distracting injury, AMS, intoxication, OR high-risk MOI with any pain. Document indication or rule-out reason.',
      nemsis: ['eExam.16'],
      feeds: ['nemsis_xml', 'narrative_engine'],
      setFindings: { slot: 'trauma', patch: { spinal_assessed: true, tourniquet_evaluated: true } },
    },
    {
      id: 'iv_access',
      kind: 'procedure',
      label: 'Two large-bore IVs (18 g or larger)',
      hint: 'Anticipate volume resuscitation. Two lines for redundancy + faster infusion. Do not delay transport for second line — establish en route if needed.',
      procedure_type: 'IV access',
      body_site: 'Bilateral AC',
      nemsis: ['eProcedures.03', 'eProcedures.05'],
      feeds: ['nemsis_xml', 'narrative_engine', 'comstar_billing'],
    },
    {
      id: 'fluid_bolus',
      kind: 'med',
      label: 'NS 1 L bolus IV (if SBP < 90)',
      hint: 'Permissive hypotension target ~90 SBP for penetrating trauma without head injury. Reassess after each 250 mL — over-resuscitation worsens dilutional coagulopathy. Head-injured: maintain SBP ≥ 110.',
      drug_name: 'Normal Saline',
      dose: '1000',
      unit: 'mL',
      route: 'IV bolus',
      responses: ['BP responding', 'BP unchanged', 'Bolus complete'],
      nemsis: ['eMedications.03', 'eMedications.04', 'eMedications.05', 'eMedications.06'],
      feeds: ['nemsis_xml', 'narrative_engine', 'comstar_billing'],
      doseGate: (ctx) => {
        const sbp = ctx.latestVital?.bp_systolic;
        if (sbp == null) return 'Document BP before bolus. Permissive-hypotension target depends on injury pattern.';
        if (sbp >= 90) {
          return `BP ${sbp} systolic at or above 90. Bolus NOT indicated for permissive-hypotension target — reassess if trending down or if head injury (target ≥ 110).`;
        }
        return null;
      },
      doseCalc: (ctx) => {
        if (ctx.weight_kg != null && ctx.weight_kg < 30) {
          const ml = Math.round(ctx.weight_kg * 20);
          return `Calc: pediatric (${ctx.weight_kg} kg) — ${ml} mL bolus (20 mL/kg).`;
        }
        return 'Calc: adult — 1 L bolus, reassess after 250 mL.';
      },
    },
    {
      id: 'fentanyl',
      kind: 'med',
      label: 'Fentanyl 50 mcg IV/IN (analgesia)',
      hint: 'For severe pain. Block on hypotension, AMS, or respiratory depression. Pediatric: 1 mcg/kg IN. Repeat q5min as needed, max 200 mcg adult.',
      drug_name: 'Fentanyl',
      dose: '50',
      unit: 'mcg',
      route: 'IV',
      responses: ['Pain decreased', 'Pain unchanged', 'Sedation', 'Respiratory depression'],
      nemsis: ['eMedications.03', 'eMedications.04', 'eMedications.05', 'eMedications.06'],
      feeds: ['nemsis_xml', 'riemsis_api', 'narrative_engine', 'comstar_billing'],
      doseGate: (ctx) => {
        const sbp = ctx.latestVital?.bp_systolic;
        const gcs = ctx.latestVital?.gcs_total;
        const rr = ctx.latestVital?.respiratory_rate;
        const spo2 = ctx.latestVital?.spo2;
        if (sbp != null && sbp < 90) {
          return `BP ${sbp} systolic is below 90. Fentanyl WITHHELD — opioid will worsen hypotension. Resuscitate first.`;
        }
        if (gcs != null && gcs < 14) {
          return `GCS ${gcs} indicates altered mental status. Fentanyl WITHHELD per RI 4.04 — risk of further depression + masking neuro changes.`;
        }
        if ((rr != null && rr < 10) || (spo2 != null && spo2 < 92)) {
          return `Respiratory depression present (RR ${rr ?? '?'}, SpO2 ${spo2 ?? '?'}%). Fentanyl WITHHELD.`;
        }
        if (sbp == null && gcs == null) {
          return 'Document BP + GCS before opioid analgesia. Protocol gates dosing on hemodynamics + mental status.';
        }
        return null;
      },
      doseCalc: (ctx) => {
        if (ctx.weight_kg != null && ctx.weight_kg < 30) {
          const peds = Math.round(ctx.weight_kg);
          return repeatableCalc(ctx, {
            perDose: peds,
            unit: 'mcg',
            intervalMin: 5,
            maxTotal: 200,
            prefix: `Pediatric (${ctx.weight_kg} kg, 1 mcg/kg IN) — `,
          });
        }
        return repeatableCalc(ctx, {
          perDose: 50,
          unit: 'mcg',
          intervalMin: 5,
          maxTotal: 200,
          prefix: 'Adult (25–50 mcg IV per dose, titrate to effect) — ',
        });
      },
    },
    {
      id: 'transport',
      kind: 'disposition',
      label: 'Transport — Level 1 Trauma Center (RIH) per RI 4.01',
      hint: 'Scene time goal ≤ 10 min for critical trauma. Activate Trauma Alert with ETA + MOI summary. Level 1 for any RI 4.01 Table 2 criterion (physiologic / anatomic / MOI).',
      destination: 'Rhode Island Hospital (Level 1 Trauma Center)',
      patch: {
        transport_decision: 'transport_this_unit',
        hospital_choice_reason: 'Trauma activation per RI 4.01 Table 2 — Level 1 Trauma Center.',
      },
      nemsis: ['eDisposition.01', 'eDisposition.02', 'eDisposition.27'],
      feeds: ['nemsis_xml', 'comstar_billing', 'toc_handoff', 'narrative_engine'],
      triggers: [{
        consumer: 'trauma_alert',
        label: 'Trauma alert activates ED + OR pre-arrival',
      }],
    },
  ],
};

/* ============================================================
 * SEED PROTOCOL — RI 2.10 Diabetic Emergency
 * ============================================================
 * Hand-built. Source: RI Statewide EMS Protocols v2026.01 §2.10.
 *
 * Critical gates:
 *   - BGL is the FIRST thing — every step downstream branches on it.
 *     Block all therapy steps until a BGL is on file. < 60 = hypo,
 *     ≥ 60 to ≤ 300 = monitor / consider DKA workup, > 300 with AMS
 *     = consider DKA / HHS, IV NS + transport.
 *   - Oral glucose 15 g PO: blocked if GCS < 14 OR no airway protection.
 *   - D10 250 mL IV: blocked if BGL ≥ 60 (no indication). Pediatric:
 *     5 mL/kg. Requires IV access (skip if not yet established).
 *   - Glucagon 1 mg IM: backup when no IV. Pediatric 0.5 mg < 25 kg.
 *   - Mandatory recheck BGL ≥ 15 min after treatment.
 */

const RI_2_10_DIABETIC: CareMatrixProtocol = {
  id: 'ri-medical-diabetic',
  name: 'Diabetic Emergency',
  version: '2026.01',
  effectiveDate: '2026-04-01',
  citation: 'RI Statewide EMS Protocol 2.10 · v2026.01',
  provenance: 'handbuilt',

  activates(epcr) {
    const haystack = [
      epcr.dispatch_reason,
      epcr.chief_complaint,
      epcr.primary_impression,
      ...(epcr.secondary_impressions ?? []),
    ].filter(Boolean).join(' ').toLowerCase();
    return /diabetic|hypoglyc|hyperglyc|dka|diabetic ketoacidosis|low blood sugar|high blood sugar|altered mental status|ams\b/.test(haystack);
  },

  steps: [
    {
      id: 'bgl',
      kind: 'procedure',
      label: 'Blood glucose check (mandatory FIRST step)',
      hint: 'Document BGL before any treatment. Every subsequent step gates on this value.',
      procedure_type: 'Blood Glucose Analysis',
      nemsis: ['eVitals.18'],
      feeds: ['nemsis_xml', 'narrative_engine'],
    },
    {
      id: 'mental_status',
      kind: 'assessment',
      label: 'Mental status + airway protection assessment',
      hint: 'Document GCS. GCS ≥ 14 + intact gag = oral glucose candidate. GCS < 14 or impaired swallow = IV/IM treatment only.',
      nemsis: ['eExam.20', 'eVitals.20'],
      feeds: ['nemsis_xml', 'narrative_engine'],
    },
    {
      id: 'oral_glucose',
      kind: 'med',
      label: 'Oral glucose 15 g PO (if alert, swallowing intact)',
      hint: 'Glucose gel or D-glucose tabs. Patient must be alert (GCS ≥ 14) and able to swallow safely. Repeat in 15 min if BGL still < 60.',
      drug_name: 'Oral Glucose',
      dose: '15',
      unit: 'g',
      route: 'PO',
      responses: ['Symptoms improved', 'BGL rose', 'Unchanged', 'Unable to tolerate'],
      nemsis: ['eMedications.03', 'eMedications.04', 'eMedications.05', 'eMedications.06'],
      feeds: ['nemsis_xml', 'riemsis_api', 'narrative_engine', 'comstar_billing'],
      doseGate: (ctx) => {
        const bgl = ctx.latestVital?.blood_glucose;
        const gcs = ctx.latestVital?.gcs_total;
        if (bgl == null) {
          return 'Document BGL before treatment. Oral glucose indicated for BGL < 60 mg/dL.';
        }
        if (bgl >= 60) {
          return `BGL ${bgl} mg/dL is at or above 60. Oral glucose NOT indicated.`;
        }
        if (gcs != null && gcs < 14) {
          return `GCS ${gcs} indicates altered mental status. Oral glucose CONTRAINDICATED — aspiration risk. Use D10 IV or glucagon IM.`;
        }
        return null;
      },
    },
    {
      id: 'iv_access',
      kind: 'procedure',
      label: 'IV access (for D10 if oral route unavailable)',
      hint: 'Saline lock. Skip if oral glucose already corrected the patient + they can self-feed.',
      procedure_type: 'IV access',
      body_site: 'Left AC',
      nemsis: ['eProcedures.03', 'eProcedures.05'],
      feeds: ['nemsis_xml', 'narrative_engine', 'comstar_billing'],
    },
    {
      id: 'd10',
      kind: 'med',
      label: 'Dextrose 10% 250 mL IV (if BGL < 60 + altered)',
      hint: 'Slow IV push over 2-3 min. Pediatric: 5 mL/kg. Recheck BGL 15 min after completion. Switch to D50 only if D10 unavailable.',
      drug_name: 'Dextrose 10%',
      dose: '250',
      unit: 'mL',
      route: 'IV',
      responses: ['Mental status improved', 'BGL rose', 'No change', 'Bolus complete'],
      nemsis: ['eMedications.03', 'eMedications.04', 'eMedications.05', 'eMedications.06'],
      feeds: ['nemsis_xml', 'riemsis_api', 'narrative_engine', 'comstar_billing'],
      doseGate: (ctx) => {
        const bgl = ctx.latestVital?.blood_glucose;
        if (bgl == null) {
          return 'Document BGL before D10. Indication is BGL < 60 mg/dL.';
        }
        if (bgl >= 60) {
          return `BGL ${bgl} mg/dL is at or above 60. D10 NOT indicated.`;
        }
        return null;
      },
      doseCalc: (ctx) => {
        if (ctx.weight_kg != null && ctx.weight_kg < 30) {
          const ml = Math.round(ctx.weight_kg * 5);
          return `Calc: pediatric (${ctx.weight_kg} kg) — ${ml} mL D10 IV (5 mL/kg).`;
        }
        return 'Calc: adult — 250 mL D10 IV. Slow push over 2-3 min.';
      },
    },
    {
      id: 'glucagon',
      kind: 'med',
      label: 'Glucagon 1 mg IM (if no IV access)',
      hint: 'Backup when IV access fails. Onset 10-15 min — slower than D10. Pediatric < 25 kg: 0.5 mg. Rotate sites if repeating.',
      drug_name: 'Glucagon',
      dose: '1',
      unit: 'mg',
      route: 'IM',
      responses: ['Mental status improved', 'BGL rose', 'No change'],
      nemsis: ['eMedications.03', 'eMedications.04', 'eMedications.05', 'eMedications.06'],
      feeds: ['nemsis_xml', 'riemsis_api', 'narrative_engine', 'comstar_billing'],
      doseCalc: (ctx) => {
        if (ctx.weight_kg != null && ctx.weight_kg < 25) {
          return `Calc: pediatric (${ctx.weight_kg} kg) — 0.5 mg IM.`;
        }
        return 'Calc: adult — 1 mg IM (anterolateral thigh).';
      },
    },
    {
      id: 'recheck_bgl',
      kind: 'procedure',
      label: 'Recheck BGL 15 min after treatment',
      hint: 'Mandatory per RI 2.10. Document response: target BGL > 80. If still < 60, repeat treatment (oral if alert, D10 if IV).',
      procedure_type: 'Blood Glucose Analysis',
      nemsis: ['eVitals.18'],
      feeds: ['nemsis_xml', 'narrative_engine'],
    },
    {
      id: 'transport',
      kind: 'disposition',
      label: 'Transport — closest ED (or refusal if fully resolved)',
      hint: 'Refusal acceptable for diabetic with full LOC return + meal eaten + reliable home situation. Document refusal-criteria checklist. Otherwise transport for evaluation of underlying cause.',
      patch: {
        transport_decision: 'transport_this_unit',
        hospital_choice_reason: 'Diabetic emergency — workup for underlying cause per RI 2.10.',
      },
      nemsis: ['eDisposition.01', 'eDisposition.02'],
      feeds: ['nemsis_xml', 'comstar_billing', 'toc_handoff', 'narrative_engine'],
    },
  ],
};

/* ============================================================
 * SEED PROTOCOL — RI 2.19 Seizure
 * ============================================================
 * Hand-built. Source: RI Statewide EMS Protocols v2026.01 §2.19.
 *
 * Critical gates:
 *   - BGL FIRST — hypoglycemia mimics seizure perfectly. < 60 →
 *     diabetic protocol activates (handled separately).
 *   - Status epilepticus definition: continuous seizure > 5 min OR
 *     recurrent seizures without return to baseline LOC between.
 *     This is the gate for benzo administration. Active seizure
 *     < 5 min = supportive care, no benzo yet.
 *   - Midazolam HARD-BLOCKS on respiratory depression (RR < 10 or
 *     SpO2 < 92) and on hypotension (SBP < 90). Both common with
 *     post-ictal patients — protect against double-suppression.
 *   - IN preferred (no IV needed, fast, atomized) — adult 5 mg.
 *   - Pediatric 0.1 mg/kg IN, max 5 mg per dose.
 *   - Repeat q5min, max 2 doses standing order (10 mg total).
 */

const RI_2_19_SEIZURE: CareMatrixProtocol = {
  id: 'ri-neuro-seizure',
  name: 'Seizure / Status Epilepticus',
  version: '2026.01',
  effectiveDate: '2026-04-01',
  citation: 'RI Statewide EMS Protocol 2.19 · v2026.01',
  provenance: 'handbuilt',

  activates(epcr) {
    const haystack = [
      epcr.dispatch_reason,
      epcr.chief_complaint,
      epcr.primary_impression,
      ...(epcr.secondary_impressions ?? []),
    ].filter(Boolean).join(' ').toLowerCase();
    return /seizure|convuls|status epilepticus|tonic.?clonic|grand mal|febrile seizure|epilep/.test(haystack);
  },

  steps: [
    {
      id: 'protect_time',
      kind: 'assessment',
      label: 'Protect patient + start seizure timer',
      hint: 'Move hard objects away. DO NOT restrain or put anything in mouth. Note start time precisely — 5-min mark drives benzo decision.',
      nemsis: ['eExam.20'],
      feeds: ['nemsis_xml', 'narrative_engine'],
    },
    {
      id: 'bgl',
      kind: 'procedure',
      label: 'Blood glucose check (rule out hypoglycemia)',
      hint: 'Hypoglycemia is the most common treatable mimic. < 60 → switch to diabetic protocol; D10 will resolve the seizure.',
      procedure_type: 'Blood Glucose Analysis',
      nemsis: ['eVitals.18'],
      feeds: ['nemsis_xml', 'narrative_engine'],
    },
    {
      id: 'airway_position',
      kind: 'procedure',
      label: 'Position + airway support (recovery position, suction, O2)',
      hint: 'Recovery (left-lateral) position when seizure stops. Suction for secretions/emesis. NRB at 15 LPM if ventilations adequate; BVM-assisted only if RR < 8 or apneic.',
      procedure_type: 'Airway Positioning',
      nemsis: ['eProcedures.03'],
      feeds: ['nemsis_xml', 'narrative_engine'],
    },
    {
      id: 'status_assessment',
      kind: 'assessment',
      label: 'Status epilepticus assessment (> 5 min OR recurrent)',
      hint: 'Status criteria: continuous > 5 min OR repeated seizures without LOC return between. Either criterion → benzo. Document which criterion met.',
      nemsis: ['eExam.20'],
      feeds: ['nemsis_xml', 'narrative_engine'],
      setFindings: { slot: 'seizure', patch: { status_assessed: true } },
    },
    {
      id: 'midazolam',
      kind: 'med',
      label: 'Midazolam 5 mg IN (or 2 mg IV) — for status epilepticus',
      hint: 'IN preferred (no IV needed, atomized device, fast onset). Repeat q5min, max 2 doses (10 mg total). Pediatric: 0.1 mg/kg IN, max 5 mg per dose.',
      drug_name: 'Midazolam (Versed)',
      dose: '5',
      unit: 'mg',
      route: 'IN',
      responses: ['Seizure stopped', 'Seizure attenuated', 'No change', 'Repeat dose given'],
      nemsis: ['eMedications.03', 'eMedications.04', 'eMedications.05', 'eMedications.06'],
      feeds: ['nemsis_xml', 'riemsis_api', 'narrative_engine', 'comstar_billing'],
      doseGate: (ctx) => {
        const sbp = ctx.latestVital?.bp_systolic;
        const rr = ctx.latestVital?.respiratory_rate;
        const spo2 = ctx.latestVital?.spo2;
        if (sbp != null && sbp < 90) {
          return `BP ${sbp} systolic is below 90. Midazolam WITHHELD — benzo will worsen hypotension. Online medical control for further direction.`;
        }
        if ((rr != null && rr < 10) || (spo2 != null && spo2 < 92)) {
          return `Respiratory depression present (RR ${rr ?? '?'}, SpO2 ${spo2 ?? '?'}%). Midazolam WITHHELD — protect airway first, support ventilation, online medical control.`;
        }
        return null;
      },
      doseCalc: (ctx) => {
        if (ctx.weight_kg != null && ctx.weight_kg < 40) {
          const peds = Math.min(5, +(ctx.weight_kg * 0.1).toFixed(1));
          return repeatableCalc(ctx, {
            perDose: peds,
            unit: 'mg',
            intervalMin: 5,
            maxDoses: 2,
            maxTotal: 10,
            prefix: `Pediatric (${ctx.weight_kg} kg, 0.1 mg/kg max 5 mg per dose) — `,
          });
        }
        return repeatableCalc(ctx, {
          perDose: 5,
          unit: 'mg',
          intervalMin: 5,
          maxDoses: 2,
          maxTotal: 10,
          prefix: 'Adult (5 mg IN — 2.5 mg per nostril, or 2 mg IV) — ',
        });
      },
    },
    {
      id: 'postictal_monitor',
      kind: 'assessment',
      label: 'Post-ictal monitoring + reassessment',
      hint: 'Document: time seizure stopped, post-ictal duration, return-to-baseline LOC, focal deficits (Todd\'s paralysis), incontinence, tongue bite. Reassess vitals q5min.',
      nemsis: ['eExam.20'],
      feeds: ['nemsis_xml', 'narrative_engine'],
    },
    {
      id: 'transport',
      kind: 'disposition',
      label: 'Transport — closest appropriate ED',
      hint: 'First-time seizure → ED workup mandatory. Known epilepsy with full recovery + on meds + family present → may consider refusal with strong documentation.',
      patch: {
        transport_decision: 'transport_this_unit',
        hospital_choice_reason: 'Seizure / status epilepticus — workup per RI 2.19.',
      },
      nemsis: ['eDisposition.01', 'eDisposition.02'],
      feeds: ['nemsis_xml', 'comstar_billing', 'toc_handoff', 'narrative_engine'],
    },
  ],
};

/* ============================================================
 * Engine → CareMatrix converter
 * ============================================================
 * The protocol-engine package (used by the protocol.emspcr.app
 * sandbox) holds 9 fully-typed RI protocols. We hand-built ACS
 * above with rich dose calculators / gates. The remaining 8 drop
 * in via this converter — same activation logic, same NEMSIS
 * fan-out, just lower-fidelity per-step calcs (which can be
 * upgraded to hand-built shape one protocol at a time).
 */

const NEMSIS_BY_KIND: Record<StepKind, string[]> = {
  med: ['eMedications.03', 'eMedications.04', 'eMedications.05', 'eMedications.06'],
  procedure: ['eProcedures.03', 'eProcedures.05', 'eProcedures.06'],
  assessment: ['eExam.01'],
  vital_set: ['eVitals.06', 'eVitals.07', 'eVitals.10', 'eVitals.14'],
  disposition: ['eDisposition.01', 'eDisposition.02'],
};

const FEEDS_BY_KIND: Record<StepKind, ConsumerId[]> = {
  med: ['nemsis_xml', 'riemsis_api', 'narrative_engine', 'comstar_billing'],
  procedure: ['nemsis_xml', 'narrative_engine', 'comstar_billing'],
  assessment: ['nemsis_xml', 'narrative_engine'],
  vital_set: ['nemsis_xml', 'narrative_engine'],
  disposition: ['nemsis_xml', 'comstar_billing', 'toc_handoff', 'narrative_engine'],
};

/** Activation predicate built from an engine protocol's triggers. */
function buildActivator(triggers: EngineTrigger[]): (epcr: EPCR) => boolean {
  const tokens = triggers
    .map((t) => t.value)
    .filter(Boolean)
    .map((v) => v.toLowerCase().trim());
  if (tokens.length === 0) return () => false;
  return (epcr) => {
    const haystack = [
      epcr.dispatch_reason,
      epcr.chief_complaint,
      epcr.primary_impression,
      ...(epcr.secondary_impressions ?? []),
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return tokens.some((tok) => haystack.includes(tok));
  };
}

function mapStepKind(t: EngineStep['type']): StepKind {
  switch (t) {
    case 'medication': return 'med';
    case 'procedure': return 'procedure';
    case 'assessment': return 'assessment';
    case 'monitor': return 'procedure';
    case 'decision': return 'assessment';
    case 'transport': return 'disposition';
  }
}

function buildLabel(s: EngineStep): string {
  if (s.type === 'medication' && s.medication) {
    const m = s.medication;
    return `${m.drugName} ${m.adultDose} ${m.adultRoute}`;
  }
  if (s.type === 'procedure' && s.procedureType) {
    return s.procedureType;
  }
  // Trim engine description to a one-liner.
  const first = s.description.split(/[.!?]\s/)[0];
  return first.length > 90 ? first.slice(0, 87) + '…' : first;
}

function engineToCareStep(s: EngineStep, protocolId: string): CareStep {
  const kind = mapStepKind(s.type);
  const id = `${protocolId}_step_${s.order}`;
  const base = {
    id,
    label: buildLabel(s),
    hint: s.plainLanguage ?? s.description,
    nemsis: NEMSIS_BY_KIND[kind],
    feeds: FEEDS_BY_KIND[kind],
  };

  if (kind === 'med' && s.medication) {
    const m = s.medication;
    const responses: string[] | undefined =
      m.repeatInterval || m.maxRepeats != null
        ? ['Improved', 'Unchanged', 'Worse', 'Pending']
        : undefined;
    const contraindications = s.contraindications.join('; ');
    const doseGate = contraindications
      ? () =>
          `Contraindications per protocol: ${contraindications}. Confirm none apply before administering.`
      : undefined;
    return {
      ...base,
      kind: 'med',
      drug_name: m.drugName,
      dose: m.adultDose,
      unit: '',
      route: m.adultRoute,
      responses,
      // doseGate here is a soft warning (returns reason every time the
      // contraindication list is non-empty so the EMT sees it). When a
      // protocol gets hand-upgraded with vitals-aware logic, swap this
      // out in place.
      ...(doseGate ? { doseGate } : {}),
    };
  }

  if (kind === 'procedure') {
    return {
      ...base,
      kind: 'procedure',
      procedure_type: s.procedureType ?? buildLabel(s),
    };
  }

  if (kind === 'disposition') {
    return {
      ...base,
      kind: 'disposition',
    };
  }

  // assessment (also covers engine `decision` steps — the hint carries
  // the question + plainQuestion so the EMT can reason it through).
  let hint = s.plainLanguage ?? s.description;
  if (s.type === 'decision' && s.decision) {
    hint = s.decision.plainQuestion || s.decision.question;
  }
  return {
    ...base,
    hint,
    kind: 'assessment',
  };
}

function engineProtocolToCareMatrix(p: EngineProtocol): CareMatrixProtocol {
  return {
    id: p.id,
    name: p.name,
    version: p.version,
    effectiveDate: p.effectiveDate,
    citation: `${p.source} — ${p.name}`,
    provenance: 'engine',
    activates: buildActivator(p.triggers),
    steps: p.steps.map((s) => engineToCareStep(s, p.id)),
  };
}

/* ============================================================
 * Catalog
 * ============================================================ */

// IDs we hand-build above; skip the engine version to avoid dupes.
// As more protocols get hand-upgraded with vitals-aware dose gates,
// add their engine ID here so the CareMatrix uses the audited version.
const HANDBUILT_IDS = new Set<string>([
  'ri-cardiac-acs',           // RI 4.04 (handbuilt as RI_4_04_ACS)
  'ri-neuro-stroke',          // RI 2.12 (handbuilt as RI_2_12_STROKE)
  'ri-medical-anaphylaxis',   // RI 2.04 (handbuilt as RI_2_04_ANAPHYLAXIS)
  'ri-cardiac-arrest',        // RI 3.03 (handbuilt as RI_3_03_CARDIAC_ARREST)
  'ri-medical-overdose',      // RI 4.18 (handbuilt as RI_4_18_OVERDOSE)
  'ri-respiratory-distress',  // RI 2.08 (handbuilt as RI_2_08_RESPIRATORY)
  'ri-trauma-hemorrhage',     // RI 4.04/4.01/4.03 (handbuilt as RI_4_04_TRAUMA)
  'ri-medical-diabetic',      // RI 2.10 (handbuilt as RI_2_10_DIABETIC)
  'ri-neuro-seizure',         // RI 2.19 (handbuilt as RI_2_19_SEIZURE)
]);

const ENGINE_CONVERTED: CareMatrixProtocol[] = RI_PROTOCOLS
  .filter((p) => !HANDBUILT_IDS.has(p.id))
  .map(engineProtocolToCareMatrix);

/**
 * Full RI Statewide EMS catalog — every protocol from the v2026.01
 * source PDF, lazy-loaded via a separate module so the heavy JSON
 * import (~2 MB) is isolated from the hot-path of care-matrix.ts
 * dependencies. Each extracted protocol participates in cross-
 * protocol satisfaction (a procedure recorded under one matched
 * protocol shows as satisfied under any sibling protocol that
 * lists the same procedure_type).
 *
 * Hand-built protocols above (RI_4_04_ACS etc.) take precedence —
 * the converter excludes their slugs to prevent duplicate entries.
 */
import { EXTRACTED_PROTOCOLS } from './extracted-protocols';

export const CARE_MATRIX_PROTOCOLS: CareMatrixProtocol[] = [
  // Hand-built (vitals-aware dose gates, weight-based calcs)
  RI_4_04_ACS,
  RI_2_12_STROKE,
  RI_2_04_ANAPHYLAXIS,
  RI_3_03_CARDIAC_ARREST,
  RI_4_18_OVERDOSE,
  RI_2_08_RESPIRATORY,
  RI_4_04_TRAUMA,
  RI_2_10_DIABETIC,
  RI_2_19_SEIZURE,
  // Engine-package converted (the 9 typed RI_PROTOCOLS, minus dupes)
  ...ENGINE_CONVERTED,
  // Full PDF-extracted catalog (~130 remaining RI protocols, prose
  // steps without dose gates — but cross-protocol satisfaction wires
  // them into the runtime so a procedure tap under any one ticks
  // every active protocol that shares the procedure_type).
  ...EXTRACTED_PROTOCOLS,
];

/** Returns the first protocol whose activation predicate matches the ePCR. */
export function activeProtocol(epcr: EPCR): CareMatrixProtocol | null {
  return CARE_MATRIX_PROTOCOLS.find((p) => p.activates(epcr)) ?? null;
}

/**
 * Returns ALL protocols whose activation predicate matches the ePCR —
 * a single chief-complaint + impression combo can legitimately invoke
 * multiple protocols (e.g. respiratory distress + anaphylaxis). UI can
 * render these as parallel matrices or a tabbed picker.
 */
export function activeProtocols(epcr: EPCR): CareMatrixProtocol[] {
  return CARE_MATRIX_PROTOCOLS.filter((p) => p.activates(epcr));
}
